import { Bell, BellOff } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import {
  disablePush,
  enablePush,
  getNotificationPermission,
  isPushSupported,
  isSubscribed,
} from "~/lib/notifications";

/**
 * Notification settings (V2-E4.1, HAN-63) — the home for the push permission +
 * subscribe flow. Lets a doctor turn push notifications on/off for the current
 * device. Permission is per-device, so the toggle reflects *this* browser's
 * subscription, not an account-wide preference.
 */
export function NotificationSettingsScreen(): JSX.Element {
  const supported = isPushSupported();
  const [permission, setPermission] = useState<NotificationPermission>(getNotificationPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reflect whether this device already has an active subscription.
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    void isSubscribed().then((value) => {
      if (!cancelled) setSubscribed(value);
    });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const handleEnable = async (): Promise<void> => {
    setBusy(true);
    try {
      const result = await enablePush();
      setPermission(getNotificationPermission());
      switch (result.status) {
        case "ok":
          setSubscribed(true);
          toast.success("Notifications are on for this device.");
          break;
        case "denied":
          toast.error("Notification permission was declined.");
          break;
        case "unsupported":
          toast.error("This browser doesn't support notifications.");
          break;
        case "unconfigured":
          toast.error("Notifications aren't available right now.");
          break;
        case "error":
          toast.error(result.message);
          break;
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async (): Promise<void> => {
    setBusy(true);
    try {
      const result = await disablePush();
      if (result.status === "ok") {
        setSubscribed(false);
        toast.success("Notifications are off for this device.");
      } else {
        toast.error(result.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage how this device works for you.</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" aria-hidden />
            Notifications
          </CardTitle>
          <CardDescription>
            Get a push notification on this device when a handover is acknowledged or an urgent task
            needs attention.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!supported && (
            <Alert>
              <AlertTitle>Not available on this browser</AlertTitle>
              <AlertDescription>
                This browser doesn&rsquo;t support push notifications. Try installing HandoverPro as
                an app, or use a different browser.
              </AlertDescription>
            </Alert>
          )}

          {supported && permission === "denied" && (
            <Alert variant="destructive">
              <AlertTitle>Notifications are blocked</AlertTitle>
              <AlertDescription>
                You&rsquo;ve blocked notifications for HandoverPro. Re-enable them in your
                browser&rsquo;s site settings, then turn them on here.
              </AlertDescription>
            </Alert>
          )}

          {supported && permission !== "denied" && (
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                {subscribed
                  ? "Notifications are on for this device."
                  : "Notifications are off for this device."}
              </p>
              {subscribed ? (
                <Button variant="outline" onClick={() => void handleDisable()} disabled={busy}>
                  <BellOff />
                  Turn off
                </Button>
              ) : (
                <Button onClick={() => void handleEnable()} disabled={busy}>
                  <Bell />
                  Enable notifications
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
